import {
    useCallback,
    useContext,
    useLayoutEffect,
    useMemo,
    type ReactNode,
} from "react";
import type { BoardPlacement } from "./Board.types";
import type { BoardCellEntry } from "./boardCells";
import {
    BOARD_CARD_CHUNK,
    BOARD_CARD_GAP,
    BOARD_CARD_OVERSCAN,
    estimateCardHeight,
} from "./boardWindowing";
import { BoardRovingContext } from "./boardRoving";
import { BoardScrollerContext, useBoardWindow } from "./useBoardWindow";
import styles from "./Board.module.scss";

interface BoardWindowedCardsProps<TItem> {
    placement: BoardPlacement;
    labelledBy: string;
    entries: ReadonlyArray<BoardCellEntry<TItem>>;
    /**
     * Cards that stay mounted off-screen, such as the dragged one. The card
     * holding focus and the board's Tab stop (`BoardRovingContext`) stay
     * mounted too.
     */
    pinnedKeys: readonly string[];
    /** A just-created card (-1 for none): scrolled to, so it mounts and enters. */
    enteringIndex: number;
    renderEntryAt: (
        index: number,
        measureRef: (node: HTMLElement | null) => void,
    ) => ReactNode;
}

// Cells on the shared lanes scroller each measure their own cards; one
// shifting the scroll for a size change jumps every other cell with it.
const NEVER_ADJUST_SCROLL = () => false;

/** A long cell's cards: only those near the viewport mount, measured. */
export const BoardWindowedCards = <TItem,>({
    placement,
    labelledBy,
    entries,
    pinnedKeys,
    enteringIndex,
    renderEntryAt,
}: Readonly<BoardWindowedCardsProps<TItem>>) => {
    const getItemKey = useCallback(
        (index: number) => entries[index].placementKey,
        [entries],
    );
    const roving = useContext(BoardRovingContext);
    const focusedKey = roving?.focusedKey ?? null;
    const activeKey = roving?.activeKey ?? null;
    const { isShared } = useContext(BoardScrollerContext);
    const pinnedIndexes = useMemo(() => {
        const pinned = new Set([...pinnedKeys, focusedKey, activeKey]);
        return entries.flatMap((entry, index) =>
            pinned.has(entry.placementKey) ? [index] : [],
        );
    }, [entries, pinnedKeys, focusedKey, activeKey]);
    const { listRef, runs, measureElement, scrollToIndex } = useBoardWindow({
        placement,
        count: entries.length,
        getItemKey,
        estimateSize: estimateCardHeight,
        gap: BOARD_CARD_GAP,
        overscan: BOARD_CARD_OVERSCAN,
        chunk: BOARD_CARD_CHUNK,
        pinnedIndexes,
        shouldAdjustScrollPositionOnItemSizeChange: isShared
            ? NEVER_ADJUST_SCROLL
            : undefined,
    });
    // Only a card outside the window needs scrolling to, to mount; a mounted
    // one scrolls itself into view as it enters. A list that just mounted
    // has no geometry to aim with until it renders its first cards.
    const mountedIndexes = runs.flatMap((run) =>
        run.kind === "item" ? [run.index] : [],
    );
    const shouldScrollToEntering =
        enteringIndex !== -1 &&
        mountedIndexes.length > 0 &&
        !mountedIndexes.includes(enteringIndex);
    useLayoutEffect(() => {
        if (shouldScrollToEntering) {
            scrollToIndex(enteringIndex, { align: "auto" });
        }
    }, [shouldScrollToEntering, enteringIndex, scrollToIndex]);

    return (
        <ul ref={listRef} aria-labelledby={labelledBy} className={styles.cards}>
            {runs.map((run) =>
                run.kind === "spacer" ? (
                    <li
                        key={run.key}
                        aria-hidden
                        className={styles.windowSpacer}
                        style={{ height: run.size }}
                    />
                ) : (
                    renderEntryAt(run.index, measureElement)
                ),
            )}
        </ul>
    );
};
